/* eslint-disable complexity */
import * as React from 'react'
import dynamic from 'next/dynamic'

import { ProductPageData } from '@thg-commerce/enterprise-aurora-product/resolvers/ProductPage/types'
import { useProductOptionsTransformer } from '@thg-commerce/enterprise-components'
import {
  Routes,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { InteractionLocation } from '@thg-commerce/enterprise-core/src/Basket'
import { useBasket } from '@thg-commerce/enterprise-core/src/Basket/hooks/useBasketId'
import { RecommendationTrackingContext } from '@thg-commerce/enterprise-metrics/src/backend_event/types/tracking'
import { pushToEventGA4 } from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import {
  MoneyValue,
  VariantOption,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { formatPrice } from '@thg-commerce/enterprise-pricing/publish/cjs'
import { ProductOptions } from '@thg-commerce/gravity-patterns'

import { useProductPage } from '../../../context'

import {
  Container,
  PlusContainer,
  PlusWrapper,
  ProductContainerDetailsContent,
  ProductOneContainer,
  ProductRowContainer,
  ProductsContainer,
  ProductText,
  ProductTwoContainer,
  StyledButton,
  StyledChevronRight,
  StyledImage,
  StyledLink,
  StyledProductOptionsRendererDiv,
  StyledProductPrice,
  SubTitle,
  SummaryContainer,
  SummaryPrice,
  Title,
} from './styles'

const GravityPlus = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Plus'),
)

const calculateTotalPrice = (
  productOnePrice: string | number,
  productTwoPrice: string | number,
  currency: string,
  shippingDestination: string,
) => {
  const totalPrice = +productOnePrice + +productTwoPrice
  return formatPrice(currency, totalPrice, shippingDestination)
}

export type ProductOptionsAndVariant = {
  title: string
  complexSku: boolean
  url?: string
  defaultImage: string
  options: VariantOption[]
  variants: ProductPageData['variants']
  cheapestVariantPrice: {
    price: MoneyValue
  }
  defaultVariant?: ProductPageData['defaultVariant']
}

export type FrequentlyBoughtTogetherProps = {
  mainProduct: ProductOptionsAndVariant
  recommendedProduct: ProductOptionsAndVariant
  i18nText: {
    frequentlyBoughtTogether: string
    thisItem: string
    recommendedItem: string
    addBothToBasket: string
    from: string
    totalPrice: string
  }
  showOutOfStockAsDisabledOnFrequentlyBoughtTogether?: boolean
  enableFBTProductOptionPlacement?: boolean
  enableFBTHalfPageStyling?: boolean
}

type ProductVariant = NonNullable<ProductPageData>['variants'][0]

const getDisabledButton = (
  product,
  recommendedVariant,
  selectedVariant,
  mainProductSku,
) =>
  (!(product?.sku && recommendedVariant?.sku) && !recommendedVariant) ||
  !product?.inStockSizes ||
  !product?.defaultVariant.inStock ||
  !selectedVariant?.inStock ||
  !recommendedVariant?.inStock ||
  Boolean(mainProductSku && mainProductSku.subscriptionContract)

const getVariantInStock = (selectedVariant, product, recommendedVariant) =>
  selectedVariant?.inStock &&
  product?.inStockSizes &&
  recommendedVariant?.inStock

const getSelectedParentVariant = (selectedVariant, product) =>
  selectedVariant?.sku || product?.defaultVariant.sku

export const FrequentlyBoughtTogether = (
  props: FrequentlyBoughtTogetherProps,
) => {
  const { product, selectedVariant, setSelectedVariant } = useProductPage()

  const { defaultLocale, defaultCurrency } = useSiteDefinition()
  const theme = useTheme()
  const {
    useExternalIdentifier,
    useExternalIdentifierInSchema,
  } = useSiteConfig()

  const productOptionsTransformer = useProductOptionsTransformer<
    ProductVariant
  >()
  const basket = useBasket()
  const [recommendedVariant, setRecommendedVariant] = React.useState<
    ProductVariant | undefined
  >(
    props.recommendedProduct?.defaultVariant ||
      props.recommendedProduct.variants.find((product) => product.inStock),
  )

  const mainProductOptionsProps = React.useMemo(
    () =>
      productOptionsTransformer({
        selectedVariant,
        options: props.mainProduct.options,
        variants: props.mainProduct.variants || [],
        variantChangedCallback: (variant) => {
          if (variant) {
            setSelectedVariant?.({
              ...variant,
              product,
            })
            handleViewItem()
          }
        },
      }),
    [
      product,
      productOptionsTransformer,
      selectedVariant,
      setSelectedVariant,
      props.mainProduct.options,
    ],
  )

  const data = {
    main: {
      sku: selectedVariant?.sku,
      title: selectedVariant?.title || props.mainProduct.title,
      image:
        selectedVariant?.images && selectedVariant?.images.length !== 0
          ? selectedVariant?.images[0]?.largeProduct
          : props.mainProduct.defaultImage ?? '',
      priceAmount:
        selectedVariant?.price?.price?.amount ??
        props.mainProduct.cheapestVariantPrice.price.amount,
      priceDisplay:
        selectedVariant?.price?.price?.displayValue ??
        props.mainProduct.cheapestVariantPrice?.price?.displayValue,
    },
    recommended: {
      sku:
        recommendedVariant?.sku ||
        (props.recommendedProduct.variants.length === 1
          ? props.recommendedProduct.variants[0].sku
          : undefined),
      title: recommendedVariant?.title ?? props.recommendedProduct.title,
      image:
        recommendedVariant?.images && recommendedVariant?.images.length !== 0
          ? recommendedVariant.images[0].largeProduct
          : props.recommendedProduct.defaultImage ?? '',
      priceAmount:
        recommendedVariant?.price?.price?.amount ??
        props.recommendedProduct.cheapestVariantPrice.price.amount,
      priceDisplay:
        recommendedVariant?.price?.price?.displayValue ??
        props.recommendedProduct.cheapestVariantPrice.price.displayValue,
    },
  }

  const GA4Items = [
    {
      item_name: data.main.title,
      item_id: data.main.sku?.toString(),
      price: data.main.priceAmount,
      item_currency: defaultCurrency,
      quantity: 1,
      item_list_name: props.i18nText.frequentlyBoughtTogether,
      item_page: InteractionLocation.PDP,
      item_external_id:
        selectedVariant.externalIdentifier || selectedVariant.sku?.toString(),
      item_variant:
        (useExternalIdentifier || useExternalIdentifierInSchema) &&
        selectedVariant.externalIdentifier
          ? selectedVariant.externalIdentifier
          : selectedVariant.sku?.toString(),
    },
    {
      item_name: data.recommended.title,
      item_id: data.recommended.sku?.toString(),
      price: data.recommended.priceAmount,
      item_currency: defaultCurrency,
      quantity: 1,
      item_list_name: props.i18nText.frequentlyBoughtTogether,
      item_page: InteractionLocation.PDP,
      item_external_id:
        recommendedVariant?.externalIdentifier ||
        data.recommended.sku?.toString(),
      item_variant:
        (useExternalIdentifier || useExternalIdentifierInSchema) &&
        recommendedVariant?.externalIdentifier
          ? recommendedVariant?.externalIdentifier
          : data.recommended.sku?.toString(),
    },
  ]

  React.useEffect(() => {
    handleViewItem()
  }, [])

  const recommendedProductOptionsProps = React.useMemo(
    () =>
      recommendedVariant?.sku &&
      productOptionsTransformer({
        selectedVariant: recommendedVariant,
        options: props.recommendedProduct.options,
        variants: props.recommendedProduct.variants || [],
        variantChangedCallback: setRecommendedVariant,
      }),
    [
      productOptionsTransformer,
      props.recommendedProduct.options,
      props.recommendedProduct.variants,
      recommendedVariant,
    ],
  )

  // @TODO: [REBUILD-4953] Investigate use of addProductsToBasket mutation and added to basket modal UX

  const variantInStock = getVariantInStock(
    selectedVariant,
    product,
    recommendedVariant,
  )
  const selectedParentVariant = getSelectedParentVariant(
    selectedVariant,
    product,
  )

  const buyBothUrl = `${Routes.Basket}?buylist=${
    variantInStock ? `${selectedParentVariant},${recommendedVariant?.sku}` : ''
  }&rctxt=${RecommendationTrackingContext.FREQUENTLY_BOUGHT_TOGETHER}`

  const mainProductSku = basket.basket?.items.find(
    (item) =>
      item.product.sku.toString() === selectedVariant?.sku.toString() ||
      item.product.sku.toString() === recommendedVariant?.sku.toString(),
  )

  const disableButton = getDisabledButton(
    product,
    recommendedVariant,
    selectedVariant,
    mainProductSku,
  )

  const handleViewItem = () => {
    pushToEventGA4({
      event: 'ecom_event',
      event_name: 'view_item',
      component: 'FBT',
      ecommerce: {
        items: GA4Items,
      },
    })
  }

  const handleAddToCart = () => {
    pushToEventGA4({
      event: 'ecom_event',
      event_name: 'add_to_cart',
      component: 'FBT',
      ecommerce: {
        items: GA4Items,
      },
    })
  }

  const renderProductsContainer = () => (
    <ProductsContainer>
      <ProductOneContainer>
        <ProductRowContainer>
          {data.main.image && (
            <StyledImage
              alt={data.main.title}
              urls={{
                largeProduct: data.main.image,
              }}
              width={
                theme.widget.productRecommendations.frequentlyBought?.image
                  ?.width
              }
            />
          )}

          <ProductContainerDetailsContent>
            <SubTitle>{props.i18nText.thisItem}</SubTitle>
            <ProductText>{data.main.title}</ProductText>
            {data.main.priceDisplay && (
              <StyledProductPrice
                price={{ defaultPrice: data.main.priceDisplay }}
                hasFromPrice={!selectedVariant && props.mainProduct.complexSku}
                fromText={props.i18nText.from}
              />
            )}
          </ProductContainerDetailsContent>
        </ProductRowContainer>
        <StyledProductOptionsRendererDiv>
          <ProductOptions
            {...mainProductOptionsProps}
            forceDropDowns={true}
            showOutOfStockAsDisabled={
              props.showOutOfStockAsDisabledOnFrequentlyBoughtTogether
            }
            enableFBTProductOptionPlacement={
              props.enableFBTProductOptionPlacement
            }
            enableFBTHalfPageStyling={props.enableFBTHalfPageStyling || false}
          />
        </StyledProductOptionsRendererDiv>
      </ProductOneContainer>
      <PlusContainer>
        <PlusWrapper>
          <GravityPlus />
        </PlusWrapper>
      </PlusContainer>
      <ProductTwoContainer>
        <ProductRowContainer>
          {data.recommended.image && (
            <StyledImage
              alt={data.recommended.title}
              urls={{
                largeProduct: data.recommended.image,
              }}
              height={
                theme.widget.productRecommendations.frequentlyBought?.image
                  ?.height
              }
              width={
                theme.widget.productRecommendations.frequentlyBought?.image
                  ?.width
              }
            />
          )}
          <StyledLink
            href={`${props.recommendedProduct.url}?rctxt=${RecommendationTrackingContext.FREQUENTLY_BOUGHT_TOGETHER}`}
          >
            <ProductContainerDetailsContent>
              <SubTitle>{props.i18nText.recommendedItem}</SubTitle>
              <ProductText productTwoText={true}>
                {data.recommended.title}
              </ProductText>
              {data.recommended.priceDisplay && (
                <StyledProductPrice
                  price={{ defaultPrice: data.recommended.priceDisplay }}
                  hasFromPrice={
                    !recommendedVariant && props.recommendedProduct.complexSku
                  }
                  fromText={props.i18nText.from}
                />
              )}
            </ProductContainerDetailsContent>
            <StyledChevronRight />
          </StyledLink>
        </ProductRowContainer>
        <StyledProductOptionsRendererDiv>
          <ProductOptions
            {...recommendedProductOptionsProps}
            forceDropDowns={true}
            showOutOfStockAsDisabled={
              props.showOutOfStockAsDisabledOnFrequentlyBoughtTogether
            }
            enableFBTProductOptionPlacement={
              props.enableFBTProductOptionPlacement
            }
            enableFBTHalfPageStyling={props.enableFBTHalfPageStyling || false}
          />
        </StyledProductOptionsRendererDiv>
      </ProductTwoContainer>
    </ProductsContainer>
  )

  return (
    <React.Fragment>
      <Title>{props.i18nText.frequentlyBoughtTogether}</Title>
      <Container data-testid="frequently-bought-together-container">
        {renderProductsContainer()}
        <SummaryContainer>
          {data.main.priceAmount && data.recommended.priceAmount && (
            <SummaryPrice
              price={{
                defaultPrice: calculateTotalPrice(
                  data.main.priceAmount,
                  data.recommended.priceAmount,
                  defaultCurrency,
                  defaultLocale,
                ),
              }}
              hasFromPrice
              fromText={
                (selectedVariant && recommendedVariant) ||
                (!props.mainProduct.complexSku &&
                  !props.recommendedProduct.complexSku)
                  ? props.i18nText.totalPrice.concat(':')
                  : props.i18nText.from.concat(':')
              }
              fbtOrientation={
                theme.widget.productRecommendations.frequentlyBought?.price
                  ?.orientation
              }
            />
          )}
          <StyledButton
            data-testid="add-both-to-basket-button"
            sizing="regular"
            emphasis={
              theme.pageTheme?.addToBasket?.addToBasketButton?.emphasis ||
              'high'
            }
            renderedAs={!disableButton ? 'a' : undefined}
            disabled={disableButton}
            href={buyBothUrl}
            onClick={handleAddToCart}
          >
            {props.i18nText.addBothToBasket}
          </StyledButton>
        </SummaryContainer>
      </Container>
    </React.Fragment>
  )
}
